import { GifID, IGif } from '@giphy/js-types'
import { create } from 'zustand'

type GifData = {
    position: number
    gif: IGif
    gridIndex: number
}

type IDsStore = {
    [id: GifID]: GifData
}

const initialState: IDsStore = {}
const useDataStore = create<IDsStore>()((_) => initialState)

type StateStore = {
    enabled: boolean
    setEnabled: (enabled: boolean) => void
    anchorIndex: number | null
}

const useEnabledStore = create<StateStore>()((set) => ({
    enabled: false,
    setEnabled: (enabled) => set({ enabled }),
    anchorIndex: null,
}))

export const useBulkEditOpen = () => useEnabledStore((state) => state.enabled)
export const setBulkEditOpen = (enabled: boolean) => useEnabledStore.setState(() => ({ enabled }))

export const useBulkEditAnchor = () => useEnabledStore((state) => state.anchorIndex)

export const setAnchorIndex = (index: number | null) => useEnabledStore.setState({ anchorIndex: index })

export const getAnchorIndex = () => useEnabledStore.getState().anchorIndex

export const useBulkEditPosition = (id: GifID): GifData => {
    return useDataStore((state) => state[id]) || 0
}

export const useBulkEditGifs = (): IDsStore => {
    return useDataStore((state) => state)
}

export const getSelectionRange = (gridIndex: number) => {
    const state = Object.entries(useDataStore.getState())
    const lowestGridIndex = state.reduce((min, [_, val]) => Math.min(val.gridIndex, min), Infinity)
    const highestGridIndex = state.reduce((max, [_, val]) => Math.max(val.gridIndex, max), -Infinity)

    if (gridIndex < lowestGridIndex) {
        return [gridIndex, lowestGridIndex]
    } else if (gridIndex > highestGridIndex) {
        return [highestGridIndex, gridIndex]
    }
    return [gridIndex, gridIndex]
}

export const addBulkEditId = (gif: IGif, gridIndex: number): void => {
    useDataStore.setState((state) => ({
        ...state,
        [gif.id]: {
            position: Object.keys(state).length + 1,
            gif,
            gridIndex,
        },
    }))
}

export const clearBulkEdit = (): void => {
    useDataStore.setState({}, true)
}

export const removeBulkEditId = (id: GifID): void => {
    useDataStore.setState((state) => {
        delete state[id]
        const newState: IDsStore = {}
        Object.values(state).forEach(({ gif, gridIndex }, index) => {
            newState[gif.id] = { gif, position: index + 1, gridIndex }
        })
        return newState
    })
}
